import { coordEq } from "@dailywars/core/engine";

import { buildUnitSelection, canOpenBuildMenu } from "#/game/selection/transitions";
import type { SelectionState } from "#/game/selection/types";
import { commitUnload, commitWait, undoLastMove } from "#/game/store/gameMutations";
import type { StoreState } from "#/game/store/types";

import type { TileSelectContext } from "./tileSelectContext";
import { commitMovePreviewDestination } from "./tileSelectMovePreviewCommit";
import { selectEnemyInspect } from "./tileSelectNoneInspect";
import {
  confirmAttackFromOptions,
  openBuildingSelectionAtTile,
  startMovePreview,
  startMovePreviewToAlly,
} from "./tileSelectSharedHelpers";

type MovePreviewSelection = Extract<SelectionState, { mode: "move-preview" }>;
type TransportUnloadSelection = Extract<SelectionState, { mode: "transport-unload" }>;
type BuildingSelection = Extract<SelectionState, { mode: "building" }>;

function handleMovePreviewEnemyBranch(
  ctx: TileSelectContext,
  sel: MovePreviewSelection,
): StoreState | undefined {
  const { state, local, clickedUnit } = ctx;
  if (!clickedUnit || clickedUnit.owner === local.currentPlayer) return undefined;
  const option = sel.attackOptions.find((o) => o.targetId === clickedUnit.id);
  if (!option) return selectEnemyInspect(ctx);
  return confirmAttackFromOptions({
    state,
    unitId: sel.unitId,
    path: sel.path,
    clickedUnit,
    forecast: option.forecast,
  });
}

export function handleMovePreviewMode(
  ctx: TileSelectContext,
  sel: MovePreviewSelection,
): StoreState {
  const { state, local, tile, clickedUnit } = ctx;

  const enemyResult = handleMovePreviewEnemyBranch(ctx, sel);
  if (enemyResult !== undefined) return enemyResult;

  if (coordEq(sel.destination, tile)) {
    return commitMovePreviewDestination({ ctx, sel });
  }

  if (sel.reachable.some((c) => coordEq(c, tile))) {
    return startMovePreview({ state, local, sel, tile });
  }

  if (clickedUnit?.owner === local.currentPlayer) {
    const isLoadOrJoin =
      sel.loadOptions.some((o) => o.targetId === clickedUnit.id) ||
      sel.joinOptions.some((o) => o.targetId === clickedUnit.id);
    if (isLoadOrJoin) {
      return startMovePreviewToAlly({ state, local, sel, ally: clickedUnit });
    }
  }

  return { ...state, selection: { mode: "none" }, previewAnimation: null };
}

export function handleTransportUnloadMode(
  ctx: TileSelectContext,
  sel: TransportUnloadSelection,
): StoreState {
  const { state, tile, clickedOwnSelectable } = ctx;

  if (clickedOwnSelectable && clickedOwnSelectable.id === sel.unitId) {
    return commitWait(state, sel.unitId);
  }

  if (sel.unloadTiles.some((c) => coordEq(c, tile))) {
    return commitUnload({ state, unitId: sel.unitId, pos: tile });
  }

  const reverted = undoLastMove(state);

  if (clickedOwnSelectable) {
    const unit = reverted.localState.units.find((u) => u.id === clickedOwnSelectable.id);
    if (unit) return { ...reverted, selection: buildUnitSelection(reverted.localState, unit) };
  }

  return reverted;
}

export function handleBuildingMode(ctx: TileSelectContext, sel: BuildingSelection): StoreState {
  const { state, local, tile, clickedOwnSelectable } = ctx;

  if (coordEq(sel.pos, tile)) return state;
  if (clickedOwnSelectable) {
    return { ...state, selection: buildUnitSelection(local, clickedOwnSelectable) };
  }
  if (canOpenBuildMenu(local, tile)) {
    return openBuildingSelectionAtTile({ state, local, tile });
  }
  return { ...state, selection: { mode: "none" }, previewAnimation: null };
}
